import { useState } from "react";
import { apiClient, base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STAGES } from "@/lib/dataroom";

export default function CreateDealDialog({ open, onOpenChange, onCreated }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [stage, setStage] = useState("due_diligence");
  const [customerId, setCustomerId] = useState("none");

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list("-created_date"),
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => apiClient.post("/deals", payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      toast.success("Datarom opprettet");
      onOpenChange(false);
      setName(""); setDescription(""); setStage("due_diligence"); setCustomerId("none");
      if (onCreated) onCreated(res.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke opprette datarom"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      stage,
      related_customer_id: customerId === "none" ? null : customerId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-deal-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-ink">Nytt datarom</DialogTitle>
          <DialogDescription>Opprett et datarom for en due diligence-prosess.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="deal-name">Navn på transaksjon</Label>
            <Input id="deal-name" data-testid="deal-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Prosjekt Nordlys" className="mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="deal-desc">Beskrivelse</Label>
            <Textarea id="deal-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Kort om transaksjonen" className="mt-1.5" rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fase</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1.5" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STAGES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Knytt til kunde (valgfritt)</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1.5" data-testid="deal-customer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
            <Button type="submit" data-testid="submit-create-deal" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Oppretter..." : "Opprett datarom"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
